import { Card, makeStyles, Badge } from "@fluentui/react-components";
import { Navigate } from "react-router-dom";

// Import theme tokens
import { tokens } from "../../theme";
import {
  useAppSelector,
  selectUser,
  selectAuthEnabled,
  selectIsGuest,
} from "../../store";

// =============================================================================
// TOKEN SHORTCUTS
// =============================================================================

const t = {
  // Colors
  themePrimary: tokens.palette["theme-Primary"],
  neutralPrimary: tokens.semantic["neutral-Primary"],
  neutralSecondary: tokens.semantic["neutral-Secondary"],
  neutralTertiaryAlt: tokens.semantic["neutral-TertiaryAlt"],
  neutralLighter: tokens.semantic["neutral-Lighter"],
  neutralLighterAlt: tokens.semantic["neutral-LighterAlt"],
  dialogBackground: tokens.components.dialogBackground,

  // Typography
  fontFamilyBase: tokens.primitives["font-Family-Base"],
  fontSizeBase200: tokens.primitives["font-Size-Base-200"],
  fontSizeBase300: tokens.primitives["font-Size-Base-300"],
  fontSizeBase400: tokens.primitives["font-Size-Base-400"],
  fontSizeBase600: tokens.primitives["font-Size-Base-600"],
  fontWeightRegular: tokens.primitives["font-Weight-Regular"],
  fontWeightMedium: tokens.primitives["font-Weight-Medium"],
  fontWeightSemiBold: tokens.primitives["font-Weight-SemiBold"],
  lineHeightBase400: tokens.primitives["lineHeight-Base-400"],
  lineHeightBase500: tokens.primitives["lineHeight-Base-500"],

  // Spacing
  spacingVerticalXs: tokens.primitives["spacing-Vertical-XS"],
  spacingVerticalS: tokens.primitives["spacing-Vertical-S"],
  spacingVerticalM: tokens.primitives["spacing-Vertical-M"],
  spacingVerticalL: tokens.primitives["spacing-Vertical-L"],
  spacingVerticalXl: tokens.primitives["spacing-Vertical-XL"],
  spacingVerticalXxl: tokens.primitives["spacing-Vertical-XXL"],
  spacingHorizontalL: tokens.primitives["spacing-Horizontal-L"],
  spacingHorizontalXl: tokens.primitives["spacing-Horizontal-XL"],

  // Radius
  radiusMedium: tokens.primitives["stroke-Radius-Medium"],
  radiusLarge: tokens.primitives["stroke-Radius-Large"],
  radiusXXLarge: tokens.primitives["stroke-RadiusXxl"],

  // Shadows
  shadowFloatingMedium: tokens.shadows["shadow-Floating-Medium"],
  shadowRestingSmall: tokens.shadows["shadow-Resting-Small"],
};

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
  container: {
    padding: t.spacingVerticalXxl,
    minHeight: "100%",

    "@media only screen and (max-width: 600px)": {
      padding: t.spacingVerticalL,
    },
  },

  card: {
    maxWidth: "720px",
    margin: "0 auto",
    backgroundColor: t.dialogBackground,
    borderRadius: t.radiusXXLarge,
    border: `1px solid ${t.neutralLighter}`,
    overflow: "hidden",
    padding: t.spacingHorizontalXl,

    "@media only screen and (max-width: 600px)": {
      padding: t.spacingHorizontalL,
    },
  },

  header: {
    marginBottom: t.spacingVerticalL,
    display: "flex",
    alignItems: "center",
    gap: t.spacingVerticalM,
    flexWrap: "wrap",
  },

  headerTitle: {
    margin: 0,
    fontFamily: t.fontFamilyBase,
    fontSize: t.fontSizeBase600,
    fontWeight: t.fontWeightSemiBold as unknown as number,
    color: t.themePrimary,
    lineHeight: t.lineHeightBase500,
  },

  content: {
    display: "flex",
    flexDirection: "column",
    gap: t.spacingVerticalL,
  },

  section: {
    backgroundColor: t.neutralLighterAlt,
    borderRadius: t.radiusLarge,
    padding: t.spacingVerticalM,
    border: `1px solid ${t.neutralLighter}`,
  },

  sectionTitle: {
    fontFamily: t.fontFamilyBase,
    fontSize: t.fontSizeBase400,
    fontWeight: t.fontWeightSemiBold as unknown as number,
    color: t.themePrimary,
    margin: 0,
    marginBottom: t.spacingVerticalM,
    lineHeight: t.lineHeightBase400,
  },

  dataList: {
    margin: 0,
    padding: 0,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: t.spacingVerticalS,

    "@media only screen and (max-width: 600px)": {
      gridTemplateColumns: "1fr",
    },
  },

  dataItem: {
    display: "flex",
    flexDirection: "column",
    padding: t.spacingVerticalS,
    borderRadius: t.radiusMedium,
    backgroundColor: t.dialogBackground,
    border: `1px solid ${t.neutralLighter}`,
    boxShadow: t.shadowRestingSmall,
  },

  dataLabel: {
    fontFamily: t.fontFamilyBase,
    fontSize: t.fontSizeBase200,
    fontWeight: t.fontWeightMedium as unknown as number,
    color: t.neutralTertiaryAlt,
    textTransform: "uppercase" as const,
    letterSpacing: "0.3px",
    marginBottom: "2px",
  },

  dataValue: {
    fontFamily: t.fontFamilyBase,
    fontSize: t.fontSizeBase300,
    fontWeight: t.fontWeightRegular as unknown as number,
    color: t.neutralPrimary,
    lineHeight: t.lineHeightBase400,
    margin: 0,
  },

  emptyValue: {
    color: t.neutralTertiaryAlt,
    fontStyle: "italic" as const,
  },

  rolesList: {
    display: "flex",
    gap: t.spacingVerticalXs,
    flexWrap: "wrap",
  },

  // Guest message styles
  guestMessageContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: t.spacingVerticalXl,
    gap: t.spacingVerticalM,
  },

  guestTitle: {
    margin: 0,
    fontFamily: t.fontFamilyBase,
    fontSize: t.fontSizeBase600,
    fontWeight: t.fontWeightSemiBold as unknown as number,
    color: t.themePrimary,
    lineHeight: t.lineHeightBase500,
  },

  guestMessage: {
    margin: 0,
    fontFamily: t.fontFamilyBase,
    fontSize: t.fontSizeBase300,
    fontWeight: t.fontWeightRegular as unknown as number,
    color: t.neutralSecondary,
    lineHeight: t.lineHeightBase400,
    maxWidth: "600px",
  },
});

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

const formatDateTime = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
};

const getRoleBadgeAppearance = (
  role: string
): "filled" | "ghost" | "outline" | "tint" => {
  switch (role) {
    case "system_admin":
      return "filled";
    case "tenant_admin":
      return "tint";
    default:
      return "outline";
  }
};

const getRoleDisplayName = (role: string): string => {
  switch (role) {
    case "system_admin":
      return "System Admin";
    case "tenant_admin":
      return "Tenant Admin";
    case "user":
      return "User";
    default:
      return role;
  }
};

// =============================================================================
// COMPONENT
// =============================================================================

export const Profile = () => {
  const styles = useStyles();
  const user = useAppSelector(selectUser);
  const authEnabled = useAppSelector(selectAuthEnabled);
  const isGuest = useAppSelector(selectIsGuest);

  const DataField = ({
    label,
    value,
  }: {
    label: string;
    value?: string | number;
  }) => (
    <div className={styles.dataItem}>
      <dt className={styles.dataLabel}>{label}</dt>
      <dd className={`${styles.dataValue} ${!value ? styles.emptyValue : ""}`}>
        {value ?? "—"}
      </dd>
    </div>
  );

  // Redirect to home if auth is disabled
  if (!authEnabled) {
    return <Navigate to="/" replace />;
  }

  // Show guest message if user is a guest
  if (isGuest) {
    return (
      <main className={styles.container}>
        <Card className={styles.card}>
          <div className={styles.guestMessageContainer}>
            <h1 className={styles.guestTitle}>Guest User</h1>
            <p className={styles.guestMessage}>
              You are currently signed in as a Guest. Your session is temporary
              and no personal information is being captured. Sign in to unlock
              your full profile and save chat history.
            </p>
          </div>
        </Card>
      </main>
    );
  }

  if (!user) {
    return (
      <main className={styles.container}>
        <Card className={styles.card}>
          <p>No user data available.</p>
        </Card>
      </main>
    );
  }

  return (
    <main className={styles.container}>
      <Card className={styles.card}>
        <header className={styles.header}>
          <h1 className={styles.headerTitle}>
            {user.displayName || "User Profile"}
          </h1>
          {!user.isEnabled && (
            <Badge appearance="filled" color="danger">
              Account Disabled
            </Badge>
          )}
        </header>

        <div className={styles.content}>
          {/* Account Information */}
          <section className={styles.section} aria-labelledby="section-account">
            <h2 id="section-account" className={styles.sectionTitle}>
              Account Information
            </h2>
            <dl className={styles.dataList}>
              <DataField label="Email" value={user.email || undefined} />
              <DataField label="User ID" value={user.id} />
              <DataField
                label="Principal ID"
                value={user.principalId || undefined}
              />
              <DataField
                label="Authentication"
                value={
                  user.authType === "authenticated"
                    ? "Authenticated"
                    : "Guest"
                }
              />
            </dl>
          </section>

          {/* Roles */}
          <section className={styles.section} aria-labelledby="section-roles">
            <h2 id="section-roles" className={styles.sectionTitle}>
              Roles
            </h2>
            <div className={styles.rolesList}>
              {user.roles.length > 0 ? (
                user.roles.map((role) => (
                  <Badge
                    key={role}
                    appearance={getRoleBadgeAppearance(role)}
                    size="large"
                  >
                    {getRoleDisplayName(role)}
                  </Badge>
                ))
              ) : (
                <span className={styles.emptyValue}>No roles assigned</span>
              )}
            </div>
          </section>

          {/* Activity */}
          <section
            className={styles.section}
            aria-labelledby="section-activity"
          >
            <h2 id="section-activity" className={styles.sectionTitle}>
              Activity
            </h2>
            <dl className={styles.dataList}>
              <DataField
                label="Last Login"
                value={formatDateTime(user.lastLoginAt)}
              />
              <DataField label="Login Count" value={user.loginCount} />
              <DataField
                label="Account Created"
                value={formatDateTime(user.createdAt)}
              />
              <DataField
                label="Last Updated"
                value={formatDateTime(user.updatedAt)}
              />
            </dl>
          </section>
        </div>
      </Card>
    </main>
  );
};

export default Profile;
